import {useRef, useState} from 'react'
import {useMossApi} from 'src/common/hooks/useMossApi'
import type {FormData} from '../components/constants'

// ─── Types ────────────────────────────────────────────────────────────────────

export type PolicySection = {
  /** Category name — matches the value selected in StepCategories */
  id: string
  /** API-generated policy text (concatenated streaming chunks) */
  policyText: string
  /** Session ID from initializePolicy response — required for updatePolicy calls */
  sessionId: string
}

// ─── spendPreferences builder (mirrors moss-frontend PolicyGeneratorBuilder) ──

const RISK_TOLERANCE_LABEL: Record<string, string> = {
  conservative: 'Conservative',
  balanced: 'Balanced',
  aggressive: 'Aggressive',
}

const SPENDING_CULTURE_LABEL: Record<string, string> = {
  strict: 'Strict',
  empowering: 'Empowering',
  friendly: 'Friendly',
}

const GUIDELINE_TONE_LABEL: Record<string, string> = {
  strict: 'Strict & policy-like',
  empowering: 'Empowering & principle-based',
  friendly: 'Friendly & trust-based',
}

const buildSpendPreferences = (fd: FormData): string => {
  const riskTolerance = RISK_TOLERANCE_LABEL[fd.riskTolerance] ?? fd.riskTolerance ?? ''
  const spendingCulture = SPENDING_CULTURE_LABEL[fd.guidelinesStyle] ?? fd.guidelinesStyle ?? ''
  const guidelineTone = GUIDELINE_TONE_LABEL[fd.guidelinesStyle] ?? fd.guidelinesStyle ?? ''

  return `Risk tolerance: ${riskTolerance} | Spending culture: ${spendingCulture} | Guideline tone: ${guidelineTone}`
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePolicyGenerator() {
  const {mossApi} = useMossApi({})

  // Stable UUID for the entire user session — sent with every API request
  const [requestGroupId] = useState(() => {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID()
    }

    return `epg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  })

  const [sections, setSections] = useState<PolicySection[]>([])
  const sectionsRef = useRef<PolicySection[]>([])
  sectionsRef.current = sections
  const [undoStack, setUndoStack] = useState<PolicySection[][]>([])
  const [redoStack, setRedoStack] = useState<PolicySection[][]>([])
  const [generating, setGenerating] = useState(false)
  const [generated, setGenerated] = useState(false)
  const [rewriting, setRewriting] = useState(false)
  const [loadingCategoryIds, setLoadingCategoryIds] = useState<string[]>([])
  const [lastAffectedSectionId, setLastAffectedSectionId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // ─── Prefetch caches (session-only, not persisted) ────────────────────────
  // Completed prefetch results keyed by category id
  const prefetchDoneRef = useRef<Map<string, PolicySection>>(new Map())
  // In-flight promises keyed by category id
  const prefetchInflightRef = useRef<Map<string, Promise<PolicySection | null>>>(new Map())
  // Guard: intro + summary are generated only once per session
  const preambleStartedRef = useRef(false)

  // Build questionnaire answers from form data — shape required by the API
  const buildQuestionnaireAnswers = (fd: FormData) => ({
    companyName: fd.companyName,
    companySize: fd.companySize,
    country: fd.country,
    industry: fd.industry,
    spendPreferences: buildSpendPreferences(fd),
  })

  // ─── Prefetch: fire initializePolicy as soon as a category is checked ─────
  const prefetch = (category: string, fd: FormData) => {
    if (prefetchDoneRef.current.has(category) || prefetchInflightRef.current.has(category)) {
      return
    }

    const questionnaireAnswers = buildQuestionnaireAnswers(fd)

    const promise: Promise<PolicySection | null> = mossApi.apiAgentGatewaySpendPolicyController
      .initializePolicy({questionnaireAnswers, category, requestGroupId})
      .then((res) => {
        if (!res.data || res.data.length === 0) {
          return null
        }

        const policyText = res.data.map((c) => c.policyText).join('')

        if (!policyText.trim()) {
          return null
        }

        return {
          id: category,
          policyText,
          sessionId: res.data[res.data.length - 1]?.sessionId ?? '',
        }
      })
      .catch(() => null)

    promise.then((result) => {
      prefetchInflightRef.current.delete(category)

      if (result) {
        prefetchDoneRef.current.set(category, result)
      }
    })

    prefetchInflightRef.current.set(category, promise)
  }

  // ─── Generate: show results per-category as each request resolves ────────
  const generate = async (
    formData: FormData,
    categories: string[],
    onCategoriesFailed?: (ids: string[]) => void,
    forceRefresh?: boolean,
  ) => {
    setError(null)

    // Existing sections (with user edits) always take priority
    // intro + summary are not in categories but must be preserved
    // forceRefresh: ignore existing sections (e.g. when tone settings changed)
    const existingSections = forceRefresh
      ? []
      : sectionsRef.current.filter((s) => categories.includes(s.id) || s.id === 'intro' || s.id === 'summary')

    const existingIds = new Set(existingSections.map((s) => s.id))
    const neededIds = categories.filter((id) => !existingIds.has(id))

    // Split needed into: already cached vs still in-flight / not started

    const fromCache = neededIds
      .filter((id) => prefetchDoneRef.current.has(id))
      .map((id) => prefetchDoneRef.current.get(id)!)

    const stillLoading = neededIds.filter((id) => !prefetchDoneRef.current.has(id))

    // Start any not yet prefetched
    stillLoading.forEach((id) => prefetch(id, formData))

    // Show immediately what we have; mark the rest as loading skeletons
    setSections([...existingSections, ...fromCache])

    // Preserve preamble loading IDs (intro/summary) so their skeletons remain visible
    setLoadingCategoryIds((prev) => {
      const preambleIds = prev.filter((id) => id === 'intro' || id === 'summary')
      return [...preambleIds, ...stillLoading]
    })

    setGenerated(true)

    if (stillLoading.length === 0) {
      if (existingSections.length === 0 && fromCache.length === 0) {
        setError('Our AI is currently at capacity. Please try again in a few minutes.')
        setGenerated(false)
      }

      return
    }

    setGenerating(true)

    // Resolve each independently — update UI as each comes in
    const results = await Promise.all(
      stillLoading.map(async (id) => {
        const promise = prefetchInflightRef.current.get(id)
        const result = promise ? await promise : null

        setLoadingCategoryIds((prev) => prev.filter((x) => x !== id))

        if (result) {
          setSections((prev) => [...prev, result])
        }

        return result
      }),
    )

    setGenerating(false)

    const totalVisible = existingSections.length + fromCache.length + results.filter(Boolean).length

    if (totalVisible === 0) {
      setError('Our AI is currently at capacity. Please try again in a few minutes.')
      setGenerated(false)
    }

    // Collect all failed ids and notify once — avoids multiple toasts per call
    const failedIds = stillLoading.filter((_, i) => !results[i])

    if (failedIds.length > 0) {
      onCategoriesFailed?.(failedIds)
    }
  }

  // ─── Update: rewrite a single section with a new instruction ─────────────
  const updateSection = async (
    sectionId: string,
    instruction: string,
    currentSections: PolicySection[],
  ): Promise<PolicySection | null> => {
    const section = currentSections.find((s) => s.id === sectionId)

    if (!section) {
      return null
    }

    try {
      const response = await mossApi.apiAgentGatewaySpendPolicyController.updatePolicy(section.sessionId, {
        category: section.id,
        currentPolicyText: section.policyText,
        selectedSuggestion: instruction,
        requestGroupId,
      })

      if (!response.data.policyText?.trim()) {
        return null
      }

      return {
        ...section,
        policyText: response.data.policyText,
        sessionId: response.data.sessionId,
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update section')
      return null
    }
  }

  // Update ALL sections with a new instruction (e.g. risk/tone change)
  const updateAllSections = async (instruction: string, currentSections: PolicySection[]) => {
    setRewriting(true)
    setLoadingCategoryIds(currentSections.map((s) => s.id))

    try {
      const updated = await Promise.all(currentSections.map((s) => updateSection(s.id, instruction, currentSections)))

      setSections((prev) => {
        const next = prev.map((s) => {
          const idx = currentSections.findIndex((cs) => cs.id === s.id)
          return idx === -1 ? s : (updated[idx] ?? s)
        })

        setUndoStack((stack) => [...stack, prev])
        setRedoStack([])
        return next
      })
    } finally {
      setRewriting(false)
      setLoadingCategoryIds([])
    }
  }

  // Save a manual snapshot (e.g. user text edits) to undo stack
  const pushSnapshot = (newSections: PolicySection[]) => {
    setSections((prev) => {
      setUndoStack((stack) => [...stack, prev])
      setRedoStack([])
      return newSections
    })
  }

  const findChangedId = (a: PolicySection[], b: PolicySection[]): string | null => {
    const changed = b.find((s) => a.find((as) => as.id === s.id)?.policyText !== s.policyText)
    return changed?.id ?? null
  }

  const undo = () => {
    setUndoStack((stack) => {
      if (stack.length === 0) {
        return stack
      }

      const prev = stack[stack.length - 1]

      setSections((cur) => {
        setRedoStack((r) => [...r, cur])
        setLastAffectedSectionId(findChangedId(cur, prev))
        return prev
      })

      return stack.slice(0, -1)
    })
  }

  const redo = () => {
    setRedoStack((stack) => {
      if (stack.length === 0) {
        return stack
      }

      const next = stack[stack.length - 1]

      setSections((cur) => {
        setUndoStack((u) => [...u, cur])
        setLastAffectedSectionId(findChangedId(cur, next))
        return next
      })

      return stack.slice(0, -1)
    })
  }

  // ─── Preamble: generate intro + summary in parallel, once per session ───────
  const generatePreamble = async (formData: FormData, onFailed?: (id: 'intro' | 'summary') => void) => {
    if (preambleStartedRef.current) {
      return
    }

    preambleStartedRef.current = true
    setLoadingCategoryIds((prev) => [...prev, 'intro', 'summary'])

    const resolveSection = (id: 'intro' | 'summary', policyText: string, sessionId: string) => {
      setSections((prev) => [...prev.filter((s) => s.id !== id), {id, policyText, sessionId}])
      setLoadingCategoryIds((prev) => prev.filter((x) => x !== id))
    }

    const clearLoading = (id: 'intro' | 'summary') => {
      setLoadingCategoryIds((prev) => prev.filter((x) => x !== id))
      onFailed?.(id)
    }

    const questionnaireAnswers = buildQuestionnaireAnswers(formData)

    const mergeChunks = (chunks: {policyText: string; sessionId: string}[]) => ({
      policyText: chunks.map((c) => c.policyText).join(''),
      sessionId: chunks[chunks.length - 1]?.sessionId ?? '',
    })

    await Promise.allSettled([
      mossApi.apiAgentGatewaySpendPolicyController
        .initializePolicyIntro({questionnaireAnswers, requestGroupId})
        .then((res) => {
          const {policyText, sessionId} = mergeChunks(res.data ?? [])

          if (policyText.trim()) {
            resolveSection('intro', policyText, sessionId)
          } else {
            clearLoading('intro')
          }
        })
        .catch(() => clearLoading('intro')),

      mossApi.apiAgentGatewaySpendPolicyController
        .initializePolicySummary({questionnaireAnswers, requestGroupId})
        .then((res) => {
          const {policyText, sessionId} = mergeChunks(res.data ?? [])

          if (policyText.trim()) {
            resolveSection('summary', policyText, sessionId)
          } else {
            clearLoading('summary')
          }
        })
        .catch(() => clearLoading('summary')),
    ])
  }

  // Sync latest section versions (e.g. after Refine) back into prefetch cache
  const updateCache = (updatedSections: PolicySection[]) => {
    updatedSections.forEach((s) => {
      prefetchDoneRef.current.set(s.id, s)
    })
  }

  const reset = () => {
    setSections([])
    setUndoStack([])
    setRedoStack([])
    setGenerated(false)
    setGenerating(false)
    setRewriting(false)
    setLoadingCategoryIds([])
    setError(null)
    prefetchDoneRef.current.clear()
    prefetchInflightRef.current.clear()
    preambleStartedRef.current = false
  }

  // Restore previously saved sections (e.g. from sessionStorage after page refresh).
  // Populates the prefetch cache so removed categories can be re-added without a new API call.
  const restore = (savedSections: PolicySection[]) => {
    setSections(savedSections)
    setGenerated(true)
    preambleStartedRef.current = savedSections.some((s) => s.id === 'intro' || s.id === 'summary')
    savedSections.forEach((s) => prefetchDoneRef.current.set(s.id, s))
  }

  return {
    requestGroupId,
    sections,
    setSections,
    generating,
    generated,
    rewriting,
    setRewriting,
    error,
    generate,
    updateSection,
    updateAllSections,
    loadingCategoryIds,
    lastAffectedSectionId,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undo,
    redo,
    pushSnapshot,
    prefetch,
    generatePreamble,
    updateCache,
    reset,
    restore,
  }
}
